import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { BuildingThreeIcon, PenIcon, UserFourIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { assignCenterEvent } from '../../../api/center-event/assign-center-event'
import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { CenterEvent } from '../../../lib/types/models/center-event/center-event'
import { CenterEventMode } from '../../../lib/types/models/center-event/center-event-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { constructCenterPath } from '../../../views/centers/utils'
import AssignCenterEventForm from './assign-center-event-form'

type Props = {
  centerEvent: CenterEvent
  mode?: CenterEventMode
}
const CenterEventBasicInfoBox = ({ centerEvent, mode = 'NORMAL' }: Props) => {
  const queryClient = useQueryClient()
  const { navigate } = useNavigator()
  const { modalState, toggleModal, closeModel } = useModalManager<'assign'>(['assign'])

  const { mutateAsync } = useMutation({
    mutationFn: assignCenterEvent,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success("Le responsable de l'évènement a été changé avec succès")
      queryClient.invalidateQueries({ queryKey: ['center-events'] })
      closeModel('assign')
    }
  })

  return (
    <InnerBox className="p-3 flex flex-row gap-2">
      <Badge
        variant="blue"
        onClick={() =>
          navigate(constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, centerEvent.center.id))
        }
        textClassName="underline"
        text={centerEvent.center?.name ?? '-'}
        iconPosition="start"
        icon={<BuildingThreeIcon color={tailwindColors.blue.DEFAULT} />}
      />

      <Badge.Label
        label="Evènement créé par : "
        value={toFullName(centerEvent.createdBy?.firstName, centerEvent.createdBy?.lastName)}
      />
      <Badge.Label
        label="Date/heure : "
        value={DateFormatter.ddMMYYYY_HHMM(centerEvent.createdAt)}
      />
      <Badge.Label
        label="Traité par : "
        value={centerEvent.assignedTo?.firstName ?? '-'}
        icon={
          mode === 'NORMAL' && (
            <PenIcon
              className="cursor-pointer hover:fill-primary-light-active"
              color={tailwindColors.primary.DEFAULT}
              onClick={() => toggleModal('assign')}
            />
          )
        }
      />

      <Modal
        isOpen={modalState.assign}
        title={'Changer de responsable'}
        icon={UserFourIcon}
        hideActions
        size="small"
        onCancel={() => closeModel('assign')}
      >
        <AssignCenterEventForm
          defaultValues={
            centerEvent.assignedTo ? { assignedToId: centerEvent.assignedTo?.id } : undefined
          }
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onConfirm: async ({ assignedToId }) => {
              await mutateAsync({
                assignedToId,
                centerEventId: centerEvent.id
              })
            },
            onCancel: () => closeModel('assign')
          })}
        />
      </Modal>
    </InnerBox>
  )
}

export default CenterEventBasicInfoBox
